import type { SwrMutationHook, SwrMutationOptions } from '@ricoh/ria.client.core/plugins/swr/hooks/mutation.type';
import type { AsyncableFunc } from '@ricoh/ria.client.core/types/core/function.type';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkError } from '@ricoh/ria.client.core/errors';
import { useToast } from '@ricoh/ria.client.core/hooks/component/popup';
import { useCoreStoreSelector } from '@ricoh/ria.client.core/plugins/store/hooks/selector';
import useSWRMutation from 'swr/mutation';

const useSwrMutation: SwrMutationHook = (...[key, fetch, { toastError = true, onError, ...options } = {}]) => {
  const { t } = useTranslation();

  const toast = useToast();

  const networkDialogState = useCoreStoreSelector((state) => state.component_dialog.network);
  const isNetworkError = useMemo<boolean>(() => Object.values(networkDialogState).some((isShow) => isShow), [networkDialogState]);

  const swrMutation = useSWRMutation<unknown>(isNetworkError ? null : key, fetch, {
    ...(options as SwrMutationOptions),

    onError: (...args) => {
      const [error] = args;

      if (error instanceof NetworkError) {
        return;
      }

      if (toastError) {
        toast.push({
          type: 'error',
          ...error.errorMessage(t),
        });
      }

      if (onError) {
        (async () => {
          await Promise.resolve((onError as AsyncableFunc)(...args));
        })();
      }
    },
  });

  return useMemo(() => swrMutation as unknown as ReturnType<SwrMutationHook>, [swrMutation]);
};

export default useSwrMutation;
